import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import environment from 'src/environments/environment';

type GtagArguments = ['js', Date] | ['config', string, GtagParams] | ['event', string, GtagParams];
type GtagFunction = (...args: GtagArguments) => void;
type GtagParams = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFunction;
  }
}

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsService {
  private initialized = false;
  private previousPageLocation?: string;

  constructor(
    private router: Router,
    private ngZone: NgZone,
    @Inject(DOCUMENT) private document: Document
  ) {}

  initialize(): void {
    if (this.initialized || !environment.analytics.enabled || !environment.analytics.measurementId) {
      return;
    }

    this.initialized = true;
    this.loadGoogleTag(environment.analytics.measurementId);
    this.gtag('js', new Date());
    this.gtag('config', environment.analytics.measurementId, {
      send_page_view: false,
      debug_mode: environment.analytics.debugMode,
    });

    this.ngZone.runOutsideAngular(() => {
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(() => {
          this.trackPageView();
        });
    });
  }

  trackEvent(eventName: string, params: GtagParams = {}): void {
    if (!this.initialized) {
      return;
    }

    this.gtag('event', eventName, params);
  }

  private trackPageView(): void {
    const pageLocation = this.getPageLocation();
    const params: GtagParams = {
      page_title: this.getPageTitle(),
      page_location: pageLocation,
    };

    if (this.previousPageLocation) {
      params['page_referrer'] = this.previousPageLocation;
    } else if (this.document.referrer) {
      params['page_referrer'] = this.document.referrer;
    }

    this.gtag('event', 'page_view', params);
    this.previousPageLocation = pageLocation;
  }

  private loadGoogleTag(measurementId: string): void {
    const windowRef = this.document.defaultView;

    if (!windowRef) {
      return;
    }

    windowRef.dataLayer = windowRef.dataLayer ?? [];
    windowRef.gtag =
      windowRef.gtag ??
      (function (): void {
        // eslint-disable-next-line prefer-rest-params -- Google gtag consumes the special arguments object, not a rest-parameter array.
        windowRef.dataLayer?.push(arguments);
      } as GtagFunction);

    if (this.document.getElementById('google-analytics-script')) {
      return;
    }

    const script = this.document.createElement('script');
    script.id = 'google-analytics-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    this.document.head.appendChild(script);
  }

  private gtag(...args: GtagArguments): void {
    this.document.defaultView?.gtag?.(...args);
  }

  private getPageLocation(): string {
    return `${this.document.location.origin}${this.getRoutePatternPath()}`;
  }

  private getRoutePatternPath(): string {
    const segments = this.getRoutePatternSegments(this.router.routerState.snapshot.root);
    return segments.length ? `/${segments.join('/')}` : '/';
  }

  private getRoutePatternSegments(snapshot: ActivatedRouteSnapshot): string[] {
    const segments: string[] = [];
    let currentSnapshot: ActivatedRouteSnapshot | null = snapshot;

    while (currentSnapshot) {
      const path = currentSnapshot.routeConfig?.path;

      if (path && path !== '**') {
        segments.push(...path.split('/').filter(Boolean));
      }

      currentSnapshot = currentSnapshot.firstChild;
    }

    return segments;
  }

  private getPageTitle(): string {
    let title = this.document.title;
    let currentSnapshot: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;

    while (currentSnapshot) {
      const routeTitle = currentSnapshot.data['title'];

      if (typeof routeTitle === 'string') {
        title = routeTitle;
      }

      currentSnapshot = currentSnapshot.firstChild;
    }

    return title;
  }
}
